import { Box, Button, Paper, Typography } from '@mui/material';
import { SystemTableFilters } from './SystemTableFilters';
import { useMemo, useState } from 'react';
import { SystemModelMap, SystemFilterOption } from '@consts/systems';
import { useStyles } from './styles/UnassignedTable.styles';
import { getFilterParams } from '@utils/filters';
import { AppRoutes } from '@consts/routes';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { type ColumnOptions, DataTable } from '@components/tables/DataTable';
import { useNameMapQuery } from '@features/redux/apis/org';
import { capitalize } from '@utils/index';
import { useSystemsQuery, useSystemsBySerialNumQuery } from '@features/redux/apis/system';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { SystemTableEffects } from './SystemTableEffects';
import { LastConnected } from '@components/LastConnected';
import { useSelector } from 'react-redux';

import type { SystemFilter } from '@/types/systems';
import type { System } from '@api/system';
import type { Location } from '@/types/locations';
import type { RootState } from '@features/redux/store';
import { ExportSystems } from '@components/buttons/ExportSystems';

const PRODUCT_ID_MAP: Record<string, string> = {
    4400: 'Graphtec CE6000(+)',
    4405: 'Graphtec CE7000',
    10: 'ADSI',
    67: 'RCMini',
};

const PAGE_SIZE = 50;

interface SystemTableProps {
    formattedLocations?: Location[];
    mode: 'assigned' | 'unassigned';
    edit?: boolean;
}

const INIT_FILTER_STATE = {
    [SystemFilterOption.Status]: {},
    [SystemFilterOption.Company]: {},
    [SystemFilterOption.Model]: {},
    [SystemFilterOption.Reseller]: {},
    [SystemFilterOption.Location]: {},
};

export const SystemTable = ({ formattedLocations, mode }: SystemTableProps) => {
    const classes = useStyles();
    const navigate = useNavigate();

    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const [filters, setFilters, clearFilters] = useLocalStorage<SystemFilter>({
        key: `${mode}_table_filters_2`,
        value: INIT_FILTER_STATE,
    });
    const [searchTerm, setSearchTerm, clearSearchTerm] = useLocalStorage<string>({
        key: `${mode}_table_search_term`,
        value: '',
    });

    const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);

    const { data: nameMap, isLoading: nameMapLoading } = useNameMapQuery({});
    const [pageNum, setPageNum] = useState(1);

    const setFilter = (key: string, value: string, selectOne?: boolean) => {
        const isActive = filters[key as SystemFilterOption][value];
        setPageNum(1);
        setFilters({
            ...filters,
            [key]: { ...(selectOne ? {} : filters[key as SystemFilterOption]), [value]: !isActive },
        });
    };

    const searchQuery = useSystemsBySerialNumQuery(
        {
            type: mode,
            serialNum: debouncedSearchTerm,
        },
        { skip: nameMapLoading || !debouncedSearchTerm }
    );
    const { data: searchData, isFetching: searchFetching } = searchQuery;

    const systemsQuery = useSystemsQuery(
        {
            ...getFilterParams(filters, {
                companies: 'companyIds',
                resellers: 'resellerIds',
                locations: 'locationIds',
            }),
            type: mode,
            pageNum: pageNum - 1,
            pageSize: PAGE_SIZE,
        },
        { skip: nameMapLoading || Boolean(searchTerm) }
    );
    const { data: systemsData, isFetching: systemsFetching } = systemsQuery;

    const systems = searchTerm ? searchData : systemsData?.data;
    const loading = nameMapLoading || (searchTerm ? searchFetching : systemsFetching);
    const totalRecords = searchTerm ? searchData?.length : systemsData?.total;

    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: 'CompanyID',
                headerName: 'Company',
                valueGetter: ({ value }) => {
                    return nameMap?.companies[value];
                },
            },
            {
                field: 'ResellerID',
                headerName: 'Reseller',
                valueGetter: ({ value }) => {
                    return nameMap?.resellers[value];
                },
            },
            {
                field: 'SerialNum',
                headerName: 'Serial Number',
            },
            {
                field: 'Model',
                headerName: 'Model',
                valueGetter: ({ value }) => {
                    return SystemModelMap[value];
                },
            },
            {
                field: 'CreatedDate',
                headerName: 'Created Date',
                formatValue: (val) => {
                    return val ? new Intl.DateTimeFormat().format(new Date(val)) : '';
                },
            },
            {
                field: 'Status',
                headerName: 'Status',
            },
            {
                field: 'LastConnected',
                headerName: 'Last Connected',
                formatValue: (val) => <LastConnected timestamp={val} />,
            },
            {
                field: 'ProductID',
                headerName: 'Cutter Type',
                formatValue: (val) => PRODUCT_ID_MAP[val] || 'Unknown',
            },
        ] as ColumnOptions<System>[];

        if (formattedLocations) {
            // Insert location column after reseller, if locations are provided
            baseColumns.splice(2, 0, {
                field: 'LocationID',
                headerName: 'Location',
                valueGetter: ({ value }) => {
                    return nameMap?.locations[value];
                },
            });
        }

        return baseColumns;
    }, [nameMap]);

    return (
        <>
            <SystemTableEffects mode={mode} queries={[searchQuery, systemsQuery]} />

            <Paper className={classes.paperContainer} elevation={4}>
                <Box height='80px' display='flex' flexDirection='row' width='100%' justifyContent='space-between'>
                    <Typography className={classes.paperTitle} component='h2' width='fit-content'>
                        {capitalize(mode)} Systems
                    </Typography>

                    <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
                        <ExportSystems
                            params={{
                                ...getFilterParams(filters, {
                                    companies: 'companyIds',
                                    resellers: 'resellerIds',
                                    locations: 'locationIds',
                                }),
                                type: mode,
                            }}
                        />
                        <Button
                            variant={'text'}
                            size={'small'}
                            className={classes.clearFiltersButton}
                            onClick={() => {
                                clearFilters();
                                clearSearchTerm();
                            }}>
                            Clear Filters
                        </Button>
                    </Box>
                </Box>

                <SystemTableFilters
                    showLocations={Boolean(formattedLocations?.length)}
                    filters={filters}
                    setFilter={setFilter}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                />

                <DataTable
                    storageKey='Unassigned-Systems'
                    page={pageNum}
                    virtualize={false}
                    rows={systems ?? []}
                    columns={columns}
                    getUniqueRowId={(row) => `${mode}-${row.StationID}`}
                    pageSize={PAGE_SIZE}
                    totalRowCount={totalRecords || 0}
                    pagination={!searchTerm}
                    onRowClick={
                        role.stores.edit && role.isAdmin
                            ? ({ row }) => navigate(`${AppRoutes.ManageSystem}?id=${row.StationID}`)
                            : undefined
                    }
                    classes={{
                        row: classes.row,
                    }}
                    onPaginate={setPageNum}
                    loading={loading}
                />
            </Paper>
        </>
    );
};
